/**
 * @fileoverview Slideout detail panel for system-web import templates.
 * @module system-web/import-template-slideout
 *
 * Responsibilities:
 * - Render overview, visibility, and audit details for a selected template
 * - Keep template mutations routed through service-layer helpers
 * - Surface draft versus published state clearly for library users
 *
 * Dependencies:
 * - `../services/systemImportTemplateService` for visibility updates
 * - `../services/systemAuditLogService` for audit history reads
 * - `@ui-admin/components/AuditLogTimeline` for audit rendering
 *
 * Architecture:
 * - Consumes a local-first template shape already hydrated by the page layer
 * - Avoids direct API-client usage inside the component
 * - Leaves CRUD ownership to the parent page and service layer
 *
 * Notes:
 * - Draft rows are editable but should not be launched into the run wizard
 * - System and department templates share one panel with scope-specific controls
 */

import React, { useState, useEffect } from 'react';
import {
  Pencil, Trash2, Copy, Shield,
  ChevronDown, ChevronUp, X, Maximize2,
} from 'lucide-react';
import { setTemplateVisibility } from '../services/systemImportTemplateService';
import type {
  ImportTemplateVisibility,
  ImportDataMode,
  ImportBehavior,
  FieldMapping,
} from '@firesquirrel/local-db';
import AuditLogTimeline from '@ui-admin/components/AuditLogTimeline';
import { fetchAuditLogs } from '../services/systemAuditLogService';
import {
  VISIBILITY_LABELS,
  VISIBILITY_COLORS,
  DATA_MODE_COLORS,
  BEHAVIOR_LABELS,
  VISIBILITIES,
  formatDate,
  FieldMappingsTable,
} from '@firesquirrel/import-editor';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export type TemplateScope = 'system' | 'department';

export interface SystemTemplateRow {
  _id: string;
  name: string;
  description?: string;
  scope: TemplateScope;
  dataMode: ImportDataMode;
  importBehavior: ImportBehavior;
  visibility: ImportTemplateVisibility;
  allowedTenantIds?: string[];
  // Shell/variant relationship — see ImportTemplate.parentTemplateId.
  // Present on variant rows; absent on shells and standalone bound templates.
  parentTemplateId?: string;
  fieldMappings?: FieldMapping[];
  fieldMappingCount: number;
  geocodingEnabled?: boolean;
  lastRunAt?: string;
  totalRuns?: number;
  createdAt?: string;
  updatedAt?: string;
  tenantId?: string;
  tenantName?: string;
  // Draft surface — projected from the full template so the grid can
  // classify drafts above shells and the slideout can show a Draft pill.
  isDraft?: boolean;
  status?: 'draft' | 'published' | 'archived';
  origin?: 'system-web' | 'tenant-web';
}

// Re-export shared types for backward compatibility
export {
  VISIBILITY_LABELS,
  VISIBILITY_COLORS,
  DATA_MODE_COLORS,
  BEHAVIOR_LABELS,
  VISIBILITIES,
  formatDate,
} from '@firesquirrel/import-editor';

// ---------------------------------------------------------------------------
// Overview Tab
// ---------------------------------------------------------------------------

function OverviewTab({ template }: { template: SystemTemplateRow }) {
  const [mappingsExpanded, setMappingsExpanded] = useState(false);
  const isSystem = template.scope === 'system';
  const mappings: FieldMapping[] = Array.isArray(template.fieldMappings) ? template.fieldMappings : [];

  return (
    <div className="space-y-4">
      {template.description && <p className="text-sm text-gray-600">{template.description}</p>}

      <div className="flex flex-wrap gap-2">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${DATA_MODE_COLORS[template.dataMode] ?? DATA_MODE_COLORS.generic}`}>
          {template.dataMode}
        </span>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 border border-gray-200">
          {BEHAVIOR_LABELS[template.importBehavior]}
        </span>
        {isSystem && template.visibility && (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${VISIBILITY_COLORS[template.visibility]}`}>
            {VISIBILITY_LABELS[template.visibility]}
          </span>
        )}
        {!isSystem && template.tenantName && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
            {template.tenantName}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <div><div className="text-gray-500 mb-0.5">Field Mappings</div><div className="font-medium text-gray-900">{template.fieldMappingCount ?? mappings.length}</div></div>
        <div><div className="text-gray-500 mb-0.5">Geocoding</div><div className="font-medium text-gray-900">{template.geocodingEnabled ? 'Enabled' : 'Disabled'}</div></div>
        <div><div className="text-gray-500 mb-0.5">Updated</div><div className="font-medium text-gray-900">{formatDate(template.updatedAt)}</div></div>
        <div><div className="text-gray-500 mb-0.5">Created</div><div className="font-medium text-gray-900">{formatDate(template.createdAt)}</div></div>
        {template.lastRunAt && <div><div className="text-gray-500 mb-0.5">Last Run</div><div className="font-medium text-gray-900">{formatDate(template.lastRunAt)}</div></div>}
        {template.totalRuns !== undefined && <div><div className="text-gray-500 mb-0.5">Total Runs</div><div className="font-medium text-gray-900">{template.totalRuns}</div></div>}
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button type="button" onClick={() => setMappingsExpanded(v => !v)}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-xs font-semibold text-gray-700">
          <span>Field Mappings ({template.fieldMappingCount ?? mappings.length})</span>
          {mappingsExpanded ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
        </button>
        {mappingsExpanded && <FieldMappingsTable mappings={mappings} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Visibility Tab (system templates only)
// ---------------------------------------------------------------------------

function VisibilityTab({ template, onSaved }: { template: SystemTemplateRow; onSaved: () => void }) {
  const [visibility, setVisibility] = useState<ImportTemplateVisibility>(template.visibility ?? 'system-only');
  const [allowedIds, setAllowedIds] = useState((template.allowedTenantIds ?? []).join('\n'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      await setTemplateVisibility(
        template._id,
        visibility,
        visibility === 'specific-tenants'
          ? allowedIds.split(/[\n,]/).map(s => s.trim()).filter(Boolean)
          : [],
      );
      setSaved(true);
      onSaved();
    } catch (err: any) {
      setError(err.message ?? 'Failed to update visibility');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
        <Shield className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>Controls which departments can see and use this template in their import grid.</span>
      </div>
      <div className="space-y-1">
        <label className="block text-xs font-semibold text-gray-700 mb-2">Visibility</label>
        {VISIBILITIES.map(v => (
          <label key={v} className="flex items-start gap-2 py-1.5 cursor-pointer">
            <input type="radio" name="visibility" value={v} checked={visibility === v}
              onChange={() => setVisibility(v)} className="mt-0.5 text-orange-600 focus:ring-orange-500" />
            <div>
              <div className="text-sm font-medium text-gray-800">{VISIBILITY_LABELS[v]}</div>
              <div className="text-xs text-gray-500">
                {v === 'system-only' && 'Only visible to system admins — not shown in any department'}
                {v === 'all-departments' && 'Available to all departments as a platform template (read-only)'}
                {v === 'specific-tenants' && 'Available only to the listed departments'}
              </div>
            </div>
          </label>
        ))}
      </div>
      {visibility === 'specific-tenants' && (
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Allowed Tenant IDs <span className="font-normal text-gray-400">(one per line or comma-separated)</span>
          </label>
          <textarea rows={4} value={allowedIds} onChange={e => setAllowedIds(e.target.value)}
            placeholder={'tenant-id-1\ntenant-id-2'}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
        </div>
      )}
      {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
      {saved && <div className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 rounded p-2">Visibility updated.</div>}
      <button type="button" onClick={handleSave} disabled={saving}
        className="px-4 py-2 bg-orange-600 text-white rounded-lg text-xs font-medium hover:bg-orange-700 disabled:opacity-50 transition-colors">
        {saving ? 'Saving…' : 'Save Visibility'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slideout root — tabs: Overview | Visibility (system only)
// ---------------------------------------------------------------------------

type SlideoutTab = 'overview' | 'visibility' | 'audit';

export function SystemImportTemplateSlideout({
  template,
  onClose,
  onEdit,
  onClone,
  onDelete,
  onFullPage,
  cloning,
  deleting,
  onVisibilityChanged,
}: {
  template: SystemTemplateRow;
  onClose: () => void;
  onEdit?: () => void;
  onClone?: () => void;
  onDelete?: () => void;
  onFullPage?: () => void;
  cloning?: boolean;
  deleting?: boolean;
  onVisibilityChanged: () => void;
}) {
  const [activeTab, setActiveTab] = useState<SlideoutTab>('overview');
  const isSystem = template.scope === 'system';

  useEffect(() => { setActiveTab('overview'); }, [template._id]);

  const tabs: { id: SlideoutTab; label: string; systemOnly?: boolean }[] = [
    { id: 'overview',   label: 'Overview' },
    { id: 'visibility', label: 'Visibility', systemOnly: true },
    { id: 'audit',      label: 'Audit Log' },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header — owns title + action buttons + close, matching TemplateSlideoutPanel pattern */}
      <div className="shrink-0 px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate flex items-center gap-1.5">
            <span className="truncate">{template.name}</span>
            {(template.isDraft === true || template.status === 'draft') && (
              <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200">Draft</span>
            )}
            {isSystem
              ? <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200">System</span>
              : <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">Department</span>
            }
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {template.dataMode} · {BEHAVIOR_LABELS[template.importBehavior]}
            {!isSystem && template.tenantName && ` · ${template.tenantName}`}
            {isSystem && template.visibility && ` · ${VISIBILITY_LABELS[template.visibility]}`}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {isSystem && onEdit && (
            <button type="button" onClick={onEdit}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
          )}
          {onClone && (
            <button type="button" onClick={onClone} disabled={cloning}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-50">
              <Copy className="h-3.5 w-3.5" /> {cloning ? 'Cloning…' : 'Clone'}
            </button>
          )}
          {isSystem && onDelete && (
            <button type="button" onClick={onDelete} disabled={deleting}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap disabled:opacity-50">
              <Trash2 className="h-3.5 w-3.5" /> {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          {onFullPage && (
            <button type="button" onClick={onFullPage}
              className="inline-flex items-center justify-center h-7 w-7 rounded-lg border border-gray-300 text-gray-500 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-600 transition-colors"
              title="Open full page">
              <Maximize2 className="h-3.5 w-3.5" />
            </button>
          )}
          <button type="button" onClick={onClose}
            className="inline-flex items-center justify-center h-7 w-7 rounded-lg border border-gray-300 text-gray-500 hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors"
            title="Close panel">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="shrink-0 flex border-b border-gray-200 px-4">
        {tabs.filter(t => !t.systemOnly || isSystem).map(tab => (
          <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
            className={`py-2.5 px-1 mr-5 text-xs font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'overview' && <OverviewTab template={template} />}
        {activeTab === 'visibility' && isSystem && (
          <VisibilityTab template={template} onSaved={onVisibilityChanged} />
        )}
        {activeTab === 'audit' && (
          <AuditLogTimeline
            documentId={template._id}
            collection="importTemplates"
            theme={{ cardBg: '', cardBorder: '', accent: '', accentText: '', textPrimary: '', textSecondary: '', expandedBg: '' } as any}
            active={activeTab === 'audit'}
            fetchFn={fetchAuditLogs}
          />
        )}
      </div>
    </div>
  );
}
